import { memo, useCallback, useContext, useMemo, useState } from 'react';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';

import PopperTooltip from 'shared/ui/Popper/PopperTooltip';
import { GenericContext } from 'shared/constants';
import type { UploadFileBaseOptions, UploadFileBaseValues } from 'shared/ui/Modal/types';
// eslint-disable-next-line import/no-cycle
import { getActiveFileTypes } from 'shared/ui/Modal/ModalUploadFile/functions';
import { BaseAttachmentDto, BaseFileTypeDto } from '@eva/types/dto';
import SpinnerSmall from 'shared/ui/Spinner/SpinnerSmall';

import type { GenericContextType } from 'types/generic-context';
import type { FileKindConfig, FilesFilterValues, FormValuesType } from '../types';

import FilesList from '../FilesList';
import {
  fileSuitable,
  getFileId,
  mixinFileType,
  getFileKindControls,
  getStoredFilesFilter,
  normalizeFilesFilter,
  removeFilesFilter,
  storeFilesFilter,
  getOperationPermissionChecker,
} from '../functions';

import FilesPlaceholder from './FilesPlaceholder';
import FilesPlaceholderWithFilter from './FilesPlaceholderWithFilter';

interface FileKindTableProps<
  F extends BaseAttachmentDto,
  FT extends BaseFileTypeDto,
  FV extends UploadFileBaseValues = UploadFileBaseValues,
  CO extends UploadFileBaseOptions<FV> = UploadFileBaseOptions<FV>,
  Config = FileKindConfig<F, FV, CO>,
> {
  files: F[];
  fileTypes: FT[];
  entityId: number;

  plain?: boolean;
  selectedFile?: F;
  readOnly?: boolean;
  fileKindConfig: Config;
  onFileUploaded: (file: F, fileKindConfig: Config) => void;
  onFileUpdated: (file: F, fileKindConfig: Config, forceReload?: boolean) => void;
  onFileDeleted: (file: F, fileKindConfig: Config) => void;
  onFileSelected: (file: F, fileKindConfig: Config) => void;
}

const FileKindTable = <F extends BaseAttachmentDto, FT extends BaseFileTypeDto>(props: FileKindTableProps<F, FT>) => {
  const {
    plain,
    files,
    fileTypes,
    fileKindConfig,
    entityId,
    selectedFile,
    readOnly,
    onFileUploaded,
    onFileUpdated,
    onFileDeleted,
    onFileSelected,
  } = props;

  const {
    fileIcon,
    fileClassName,
    fileLabel,
    operations,
    imageNoFiles,
    UploadComponent,
    FilterComponent,
    getAllowedFiles,
    getUploadComponentOptions,
    getFileLabel,
  } = fileKindConfig;

  const [uploading, setUploading] = useState(false);
  const [formValues, setFormValues] = useState<FormValuesType | AnyObject>({});

  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const [filterValues, setFilterValues] = useState<FilesFilterValues>(
    getStoredFilesFilter(fileKindConfig.fileTypeIdKey, entityId),
  );

  const filterIsEmpty = isEmpty(filterValues);

  const context = useContext(GenericContext) as GenericContextType;

  const { isAllowedOperation, userGroupIds } = context;

  const isCreateAllowed = useMemo(() => {
    const checkCreateAllowed = getOperationPermissionChecker(operations.create, isAllowedOperation, userGroupIds);

    return checkCreateAllowed();
  }, [operations, userGroupIds, isAllowedOperation]);

  const activeFileTypes = useMemo(() => getActiveFileTypes(fileTypes), [fileTypes]);

  const fullFiles = useMemo(() => files.map((file) => mixinFileType(file, fileTypes)), [files, fileTypes]);

  const allowedFiles = useMemo(
    () =>
      typeof getAllowedFiles === 'function' ? getAllowedFiles(fullFiles, isAllowedOperation, userGroupIds) : fullFiles,
    [fullFiles, userGroupIds, isAllowedOperation, getAllowedFiles],
  );

  const filteredFiles = useMemo(
    () => (isEmpty(filterValues) ? allowedFiles : allowedFiles.filter((file) => fileSuitable(file, filterValues))),
    [allowedFiles, filterValues],
  );

  const toggleUploadDialog = useCallback(() => {
    setUploadDialogOpen(!uploadDialogOpen);
  }, [uploadDialogOpen]);

  const handleUploadFile = useCallback(
    <FV extends UploadFileBaseValues>(values: FV) => {
      setFormValues(values as unknown as AnyObject);

      if (!operations.create) {
        return Promise.reject('No create operation provided');
      }

      setUploading(true);

      return operations.create
        .action(entityId, values)
        .then((response) => {
          if (typeof onFileUploaded === 'function') {
            onFileUploaded(response, fileKindConfig);
          }
        })
        .finally(() => setUploading(false));
    },
    [entityId, fileKindConfig, operations, onFileUploaded],
  );

  const handleParseFile = useCallback(
    (file: F) => {
      if (!operations.parse) {
        return Promise.reject('No parse operation provided');
      }

      return operations.parse.action(entityId, getFileId(file)).then(() => {
        if (typeof onFileUpdated === 'function') {
          onFileUpdated({ ...file, parsed: true }, fileKindConfig);
        }
      });
    },
    [entityId, fileKindConfig, operations, onFileUpdated],
  );

  const handleUpdateFile = useCallback(
    <FV extends UploadFileBaseValues>(file: F, values: FV) => {
      if (!operations.update) {
        return Promise.reject('No update operation provided');
      }
      return operations.update.action(entityId, getFileId(file), values).then((response) => {
        if (typeof onFileUpdated === 'function') {
          onFileUpdated(response, fileKindConfig);
        }
      });
    },
    [entityId, fileKindConfig, operations, onFileUpdated],
  );

  const handleDeleteFile = useCallback(
    (file: F) => {
      if (!operations.delete) {
        return Promise.reject('No delete operation provided');
      }
      return operations.delete.action(entityId, getFileId(file)).then(() => {
        onFileDeleted(file, fileKindConfig);
      });
    },
    [entityId, fileKindConfig, operations, onFileDeleted],
  );

  const handleObtainUrl = useCallback(
    (file: F) => {
      if (!operations.obtainUrl) {
        return Promise.reject('No obtain url operation provided');
      }

      const isAllowed =
        typeof operations.obtainUrl.isAllowed === 'function'
          ? operations.obtainUrl.isAllowed(isAllowedOperation, file, userGroupIds)
          : operations.obtainUrl.isAllowed;

      if (!isAllowed) {
        return Promise.resolve('');
      }

      return file.url
        ? Promise.resolve(file.url)
        : operations.obtainUrl.action(entityId, getFileId(file)).then((url) => {
            if (typeof onFileUpdated === 'function') {
              onFileUpdated({ ...file, url }, fileKindConfig);
            }
            return Promise.resolve(url);
          });
    },
    [entityId, operations, fileKindConfig, userGroupIds, isAllowedOperation, onFileUpdated],
  );

  const handleSelectFile = useCallback(
    (file: F) => handleObtainUrl(file).then((url) => onFileSelected({ ...file, url }, fileKindConfig)),
    [fileKindConfig, handleObtainUrl, onFileSelected],
  );

  const handleApplyFilter = useCallback(
    (values: FilesFilterValues) => {
      const valuesToSet = normalizeFilesFilter(values);

      setFilterValues(valuesToSet);
      storeFilesFilter(fileKindConfig.fileTypeIdKey, entityId, valuesToSet);
    },
    [fileKindConfig.fileTypeIdKey, entityId],
  );

  const handleClearFilter = useCallback(() => {
    setFilterValues({});
    removeFilesFilter(fileKindConfig.fileTypeIdKey, entityId);
  }, [fileKindConfig.fileTypeIdKey, entityId]);

  const filterIconClassName = classnames({
    fa: true,
    'fa-filter': true,
    'text-dark-blue': !filterIsEmpty,
  });

  const uploadModalOptions =
    typeof getUploadComponentOptions === 'function' ? getUploadComponentOptions(props, context) : null;

  const uploadModal = UploadComponent ? (
    <UploadComponent
      show={uploadDialogOpen}
      options={uploadModalOptions}
      onHide={toggleUploadDialog}
      onSubmit={handleUploadFile}
    />
  ) : null;

  const filter = FilterComponent ? (
    <FilterComponent
      fileTypes={activeFileTypes}
      initialValues={filterValues}
      onSubmit={handleApplyFilter}
      onClearFilter={handleClearFilter}
    >
      <button type="button" className="btn btn-sm btn-box-tool no-padding files-filter-button" disabled={uploading}>
        <i className={filterIconClassName} />
      </button>
    </FilterComponent>
  ) : null;

  const placeholder = filterIsEmpty ? (
    <FilesPlaceholder
      fileLabel={fileLabel}
      imageNoFiles={imageNoFiles}
      canCreate={!readOnly && isCreateAllowed}
      onStartCreate={toggleUploadDialog}
    />
  ) : (
    <FilesPlaceholderWithFilter onClearFilter={handleClearFilter} />
  );

  const itemControls = useMemo(
    () =>
      getFileKindControls(
        operations,
        activeFileTypes,
        userGroupIds,
        handleObtainUrl,
        handleParseFile,
        handleUpdateFile,
        handleDeleteFile,
        isAllowedOperation,
        formValues as unknown as FormValuesType,
        readOnly,
      ),
    [
      readOnly,
      operations,
      userGroupIds,
      activeFileTypes,
      handleObtainUrl,
      handleParseFile,
      handleUpdateFile,
      handleDeleteFile,
      isAllowedOperation,
      formValues,
    ],
  );

  const content = (
    <FilesList
      plain={plain}
      files={filteredFiles}
      fileTypes={fileTypes}
      placeholder={placeholder}
      itemControls={itemControls}
      selectedFile={selectedFile}
      selectFile={handleSelectFile}
    />
  );

  const { settings } = useContext(GenericContext) as GenericContextType;
  const label = getFileLabel ? getFileLabel(settings) : translate(`${fileLabel}s`);

  return (
    <div className={`panel-block files-wrapper ${fileClassName || ''}`}>
      {uploadModal}
      <div className="file-section-header">
        <span className="pull-right file-header-buttons">
          {filter}

          {!readOnly &&
            isCreateAllowed &&
            (uploading ? (
              <SpinnerSmall />
            ) : (
              <PopperTooltip
                placement="left"
                overlay={translate('Upload {{fileLabel}}', {
                  fileLabel,
                })}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-box-tool text-success no-padding files-upload-button margin-left"
                  disabled={uploading}
                  onClick={toggleUploadDialog}
                >
                  <i className="lnr lnr-plus-circle" />
                </button>
              </PopperTooltip>
            ))}
        </span>
        <h3 className="text-primary files-header" title={label}>
          <i className={`${fileIcon || 'lnr lnr-paperclip'} margin-right files-header-icon`} />
          {label}
        </h3>
        <span className="files-under-item files-under-item-dark" />
        <span className="files-under-item files-under-item-light" />
      </div>
      <div className="section files-content">{content}</div>
    </div>
  );
};

// eslint-disable-next-line import/no-default-export
export default memo(FileKindTable);
